import { useConvexAuth } from "convex/react";
import {
  createFileRoute,
  Outlet,
  redirect,
  useNavigate,
} from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useAuth } from "@workos-inc/authkit-react";
import { usePostHogIdentify } from "@/utils/posthog";
import { convexQuery } from "@convex-dev/react-query";
import { api } from "@cvx/_generated/api";
import { safeRedirectPath } from "@/utils/safeRedirectPath";

export const Route = createFileRoute("/_app/_auth")({
  beforeLoad: async ({ context, location }) => {
    const user = await context.queryClient.ensureQueryData(
      convexQuery(api.users.queries.getCurrentUser, {}),
    );
    if (!user) {
      // On a hard load this runs before the Convex client has its auth token,
      // so an authenticated user also lands here. Carry the requested page so
      // login can return to it instead of dropping every deep link on the
      // dashboard (MOR-1283 QA).
      throw redirect({
        to: "/login",
        search: { redirect: safeRedirectPath(location.href) },
      });
    }
    return { user };
  },
  component: AuthLayout,
});

/**
 * Auth guard that protects all routes under /_app/_auth.
 *
 * Uses both Convex auth state and WorkOS auth state to avoid false redirects
 * during token refresh cycles. The Convex auth provider has a transient gap
 * where isAuthenticated briefly becomes false when the token is being refreshed
 * (effect cleanup sets state to false before the new token is confirmed).
 * WorkOS still holds the valid user during this window, so we check both
 * before redirecting to login.
 *
 * A 3-second timeout acts as a safety net: if Convex remains definitively
 * unauthenticated for longer than a normal refresh cycle (~200ms), we redirect
 * regardless of WorkOS state (which can become stale after refresh failures).
 */
function AuthLayout() {
  const { isAuthenticated, isLoading } = useConvexAuth();
  const { isLoading: isWorkOSLoading, user: workOSUser } = useAuth();
  const navigate = useNavigate();
  const redirectedRef = useRef(false);

  usePostHogIdentify();

  const convexDefinitelyOut = !isLoading && !isAuthenticated;
  const [unauthSeconds, setUnauthSeconds] = useState(0);

  useEffect(() => {
    if (!convexDefinitelyOut) {
      setUnauthSeconds(0);
      redirectedRef.current = false;
      return;
    }
    const timer = setInterval(() => setUnauthSeconds((s) => s + 1), 1000);
    return () => clearInterval(timer);
  }, [convexDefinitelyOut]);

  const isFullyUnauthenticated =
    !isLoading && !isAuthenticated && !isWorkOSLoading && !workOSUser;

  // WorkOS React state can become stale after a refresh failure (the provider
  // only updates on success). If Convex has been out for >3s, redirect anyway.
  const isStaleAuth = convexDefinitelyOut && unauthSeconds >= 3;

  useEffect(() => {
    if ((isFullyUnauthenticated || isStaleAuth) && !redirectedRef.current) {
      redirectedRef.current = true;
      navigate({
        to: "/login",
        search: {
          redirect: safeRedirectPath(
            `${window.location.pathname}${window.location.search}${window.location.hash}`,
          ),
        },
      });
    }
  }, [isFullyUnauthenticated, isStaleAuth, navigate]);

  if (isLoading || !isAuthenticated) {
    return null;
  }

  return <Outlet />;
}
